import VideoGeneration from '@/services/VideoGeneration'
import { getResponse } from '@/plugins/store'

export default {
  // Upload image
  async uploadImage({ commit }, { file }) {
    commit('UPLOAD_IMAGE_REQUEST')
    try {
      const { data } = await VideoGeneration.uploadImage(file)
      const result = getResponse(data) || {}
      commit('UPLOAD_IMAGE_SUCCESS', result)
      return { success: true, data: result }
    } catch (error) {
      commit('UPLOAD_IMAGE_FAILURE', error)
      return { success: false, error }
    }
  },

  // Set uploaded image data
  setUploadedImage({ commit }, imageData) {
    commit('SET_UPLOADED_IMAGE', imageData)
  },

  // Clear uploaded image
  clearUploadedImage({ commit }) {
    commit('CLEAR_UPLOADED_IMAGE')
  },

  // Clear errors
  clearErrors({ commit }) {
    commit('CLEAR_ERRORS')
  },

  // Generate image prompt
  async generateImagePrompt({ commit }, { imageUrl }) {
    commit('GENERATE_IMAGE_PROMPT_REQUEST')
    try {
      const { data } = await VideoGeneration.generateImagePrompt(imageUrl)
      const result = getResponse(data) || ''
      commit('GENERATE_IMAGE_PROMPT_SUCCESS', result)
      return { success: true, data: result }
    } catch (error) {
      commit('GENERATE_IMAGE_PROMPT_FAILURE', error)
      return { success: false, error }
    }
  },

  // Generate video
  async generateVideo({ commit }, { imageUrl, prompt }) {
    commit('GENERATE_VIDEO_REQUEST')
    try {
      const { data } = await VideoGeneration.generateVideo(imageUrl, prompt)
      const result = getResponse(data) || {}
      commit('GENERATE_VIDEO_SUCCESS', result)
      return { success: true, data: result }
    } catch (error) {
      commit('GENERATE_VIDEO_FAILURE', error)
      return { success: false, error }
    }
  },

  // Poll video generation status
  async pollVideoStatus({ commit }, { taskIds }) {
    commit('POLL_VIDEO_STATUS_REQUEST')
    try {
      const { data } = await VideoGeneration.pollVideoStatus(taskIds)
      const result = getResponse(data) || {}
      commit('POLL_VIDEO_STATUS_SUCCESS', result)
      return { success: true, data: result }
    } catch (error) {
      commit('POLL_VIDEO_STATUS_FAILURE', error)
      return { success: false, error }
    }
  },

  // Start polling for video status
  startVideoPolling({ commit, dispatch }, { taskIds, interval = 5000 }) {
    // Clear any existing polling
    commit('STOP_VIDEO_POLLING')

    // Start new polling interval
    const pollingInterval = setInterval(async () => {
      await dispatch('pollVideoStatus', { taskIds })
      // TODO: Add condition to stop polling when video is ready
      // if (result.data && result.data.status === 'completed') {
      //   commit('STOP_VIDEO_POLLING')
      // }
    }, interval)

    commit('SET_POLLING_INTERVAL', pollingInterval)
  },

  // Stop polling for video status
  stopVideoPolling({ commit }) {
    commit('STOP_VIDEO_POLLING')
  },

  // Selected videos actions
  setSelectedVideos({ commit }, videos) {
    commit('SET_SELECTED_VIDEOS', videos)
  },
  addSelectedVideo({ commit }, video) {
    commit('ADD_SELECTED_VIDEO', video)
  },
  removeSelectedVideo({ commit }, videoId) {
    commit('REMOVE_SELECTED_VIDEO', videoId)
  },
  clearSelectedVideos({ commit }) {
    commit('CLEAR_SELECTED_VIDEOS')
  },

  // Generate form video
  async generateFormVideo({ commit }, payload) {
    commit('GENERATE_FORM_VIDEO_REQUEST')
    try {
      const { data } = await VideoGeneration.generateFormVideo(payload)
      const result = getResponse(data) || {}
      commit('GENERATE_FORM_VIDEO_SUCCESS', result)
      return { success: true, data: result }
    } catch (error) {
      commit('GENERATE_FORM_VIDEO_FAILURE', error)
      return { success: false, error }
    }
  },

  // Upload video source
  async uploadVideoSource({ commit }, { file }) {
    commit('UPLOAD_VIDEO_SOURCE_REQUEST')
    try {
      const { data } = await VideoGeneration.uploadVideoSource(file)
      const result = getResponse(data) || {}
      commit('UPLOAD_VIDEO_SOURCE_SUCCESS', result)
      return { success: true, data: result }
    } catch (error) {
      commit('UPLOAD_VIDEO_SOURCE_FAILURE', error)
      return { success: false, error }
    }
  },

  // Video list actions
  async fetchVideoList({ commit }, {
    page = 1,
    limit = 15,
    type,
    types,
  } = {}) {
    try {
      commit('FETCH_VIDEO_LIST_REQUEST')

      const { data } = await VideoGeneration.fetchVideoList({
        page,
        limit,
        type,
        types,
      })

      if (data && !!data?.result?.content) {
        const {
          content: videos, page: responsePage, limit: responseLimit, total,
        } = data.result

        // Transform API response to match our video structure
        const transformedVideos = videos.map((video, index) => ({
          id: video._id || `api-video-${index}`, // eslint-disable-line no-underscore-dangle
          type: 'video-generation',
          sourceSectionId: video.localTaskId || `api-section-${index}`,
          imageUrl: video.thumbnailUrl || 'https://placehold.co/900x1600/PNG?text=image',
          prompt: video.promptSetting?.textPrompt || 'Generated video from API',
          videoGeneration: {
            loading: false,
            error: '',
            result: video,
            taskId: video.taskId || video._id, // eslint-disable-line no-underscore-dangle
          },
          videoPolling: {
            loading: false,
            error: '',
            result: null,
            status: video.status || 'SUCCESS',
            videoUrl: video.previewUrl,
            pollingInterval: null,
          },
          videoSelected: false,
          createdAt: video.createdAt,
          updatedAt: video.updatedAt,
          // Additional video info
          additionalVideoInfo: video.additionalVideoInfo,
          aigcVideoType: video.aigcVideoType,
          grantServiceType: video.grantServiceType,
        }))

        commit('FETCH_VIDEO_LIST_SUCCESS', {
          data: transformedVideos,
          pagination: {
            page: responsePage || page,
            limit: responseLimit || limit,
            total: total || transformedVideos.length,
            totalPages: Math.ceil((total || transformedVideos.length) / (responseLimit || limit)),
          },
        })
      } else {
        throw new Error(data?.message || 'Failed to fetch videos')
      }
    } catch (error) {
      commit('FETCH_VIDEO_LIST_FAILURE', error.response?.data?.message || error.message || 'Failed to fetch videos')
      throw error
    }
  },

  async refreshVideoList({ dispatch, state }) {
    await dispatch('fetchVideoList', {
      page: state.videoList.pagination.page,
      limit: state.videoList.pagination.limit,
    })
  },

  async loadMoreVideos({ dispatch, state }) {
    const nextPage = state.videoList.pagination.page + 1
    if (nextPage <= state.videoList.pagination.totalPages) {
      await dispatch('fetchVideoList', {
        page: nextPage,
        limit: state.videoList.pagination.limit,
      })
    }
  },

  addVideoToList({ commit }, video) {
    commit('ADD_VIDEO_TO_LIST', video)
  },

  updateVideoInList({ commit }, { id, updates }) {
    commit('UPDATE_VIDEO_IN_LIST', { id, updates })
  },

  removeVideoFromList({ commit }, id) {
    commit('REMOVE_VIDEO_FROM_LIST', id)
  },

  clearVideoList({ commit }) {
    commit('CLEAR_VIDEO_LIST')
  },

  // AI Analyze action
  async analyzeImageWithAI({ commit }, { imageUrl }) {
    try {
      commit('SET_AI_ANALYZE_LOADING', true)
      commit('CLEAR_AI_ANALYZE_ERROR')

      const { data } = await VideoGeneration.analyzeImageWithAI(imageUrl)

      if (data.result) {
        commit('SET_AI_ANALYZE_RESULT', data.result)
        return { success: true, data: data.result }
      }
      throw new Error('No result data received from API')
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to analyze image with AI'
      commit('SET_AI_ANALYZE_ERROR', errorMessage)
      return { success: false, error: { message: errorMessage } }
    } finally {
      commit('SET_AI_ANALYZE_LOADING', false)
    }
  },

  // Fetch prompt suggestions
  async fetchPromptSuggestions({ commit }, params = { limit: 200 }) {
    commit('FETCH_PROMPT_SUGGESTIONS_REQUEST')
    try {
      const { data } = await VideoGeneration.fetchPromptSuggestions(params)
      const suggestions = data?.result?.data || {}
      commit('FETCH_PROMPT_SUGGESTIONS_SUCCESS', suggestions)
      return { success: true, data: suggestions }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch prompt suggestions'
      commit('FETCH_PROMPT_SUGGESTIONS_FAILURE', errorMessage)
      return { success: false, error: { message: errorMessage } }
    }
  },

  // Generate image from image (Image to Image)
  async generateImageFromImage({ commit }, payload) {
    commit('START_IMAGE_GENERATION_PROCESS')
    commit('GENERATE_IMAGE_REQUEST')
    try {
      const { data } = await VideoGeneration.generateImageFromImage(payload)
      commit('GENERATE_IMAGE_SUCCESS', data)
      return { success: true, data }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate image'
      commit('GENERATE_IMAGE_FAILURE', errorMessage)
      commit('END_IMAGE_GENERATION_PROCESS')
      return { success: false, error: { message: errorMessage } }
    }
  },

  // Poll image generation status
  async pollImageGenerationStatus({ commit }, imageGenerationIds) {
    try {
      const { data } = await VideoGeneration.pollImageGenerationStatus(imageGenerationIds)
      commit('POLL_IMAGE_GENERATION_SUCCESS', data)
      return { success: true, data }
    } catch (error) {
      const errorMessage = error?.data?.message || 'Failed to poll image generation status'
      commit('POLL_IMAGE_GENERATION_FAILURE', errorMessage)
      return { success: false, error: { message: errorMessage } }
    }
  },

  // Start polling image generation
  async startImageGenerationPolling({ commit, dispatch }, imageGenerationId) {
    // Clear any existing polling
    commit('CLEAR_IMAGE_GENERATION_POLLING_INTERVAL')

    // Set loading state for the entire polling process
    commit('POLL_IMAGE_GENERATION_REQUEST')

    const pollInterval = setInterval(async () => {
      try {
        const result = await dispatch('pollImageGenerationStatus', imageGenerationId)
        // Check if the polling request itself failed
        if (!result.success) {
          // Polling request failed, stop polling and show error
          const errorMessage = result.error?.message || 'Failed to poll image generation status'
          commit('POLL_IMAGE_GENERATION_FAILURE', errorMessage)
          commit('CLEAR_IMAGE_GENERATION_POLLING_INTERVAL')
          commit('END_IMAGE_GENERATION_PROCESS')
          return
        }

        const status = result?.data?.result?.content?.[0]?.status

        if (status === 'DONE') {
          // Image generation completed
          const content = result?.data?.result?.content?.[0]
          const generatedImage = content?.resultImages?.[0]

          // Stop polling first
          commit('CLEAR_IMAGE_GENERATION_POLLING_INTERVAL')

          if (generatedImage) {
            // Set the image URL first (without prompt)
            commit('SET_GENERATED_IMAGE_URL', {
              url: generatedImage,
              promptText: null,
            })

            // Generate prompt for the generated image (after polling stops)
            const promptResult = await dispatch('generateImagePrompt', { imageUrl: generatedImage })
            const promptText = promptResult.success ? promptResult.data : ''

            // Update with the generated prompt
            commit('SET_GENERATED_IMAGE_URL', {
              url: generatedImage,
              promptText,
            })
          }

          commit('END_IMAGE_GENERATION_PROCESS')
        } else if (status === 'FAILED' || status === 'ERROR') {
          // Image generation failed
          const errorMessage = 'Image generation failed'
          commit('POLL_IMAGE_GENERATION_FAILURE', errorMessage)
          commit('CLEAR_IMAGE_GENERATION_POLLING_INTERVAL')
          commit('END_IMAGE_GENERATION_PROCESS')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Image generation polling error:', error)
        const errorMessage = error.message || 'An unexpected error occurred during polling'
        commit('POLL_IMAGE_GENERATION_FAILURE', errorMessage)
        commit('CLEAR_IMAGE_GENERATION_POLLING_INTERVAL')
        commit('END_IMAGE_GENERATION_PROCESS')
      }
    }, 3000) // Poll every 3 seconds

    commit('SET_IMAGE_GENERATION_POLLING_INTERVAL', pollInterval)
  },

  // Stop polling image generation
  stopImageGenerationPolling({ commit }) {
    commit('CLEAR_IMAGE_GENERATION_POLLING_INTERVAL')
    commit('END_IMAGE_GENERATION_PROCESS')
  },
}
